import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Stack,
  Breadcrumbs,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useBreadcrumb } from "../../context/BreadcrumbContext";

const EXPANDED_WIDTH = 260;
const COLLAPSED_WIDTH = 72;

interface HeaderProps {
  title: string;
  handleDrawerToggle: () => void;
  logout: () => void;
  userName?: string;
  userInitials?: string;
  sidebarCollapsed?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  title,
  handleDrawerToggle,
  logout,
  userName,
  userInitials,
  sidebarCollapsed,
}) => {
  const { detail, parentPath } = useBreadcrumb();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const drawerWidth = sidebarCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        transition: "width 0.2s ease, margin-left 0.2s ease",
        bgcolor: "#FFFFFF",
        borderBottom: "1px solid",
        borderColor: "divider",
        boxShadow: "none",
      }}
    >
      <Toolbar sx={{ minHeight: 64, px: { xs: 2, md: 3 } }}>
        <IconButton
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 2, display: { md: "none" }, color: "text.primary" }}
        >
          <MenuIcon />
        </IconButton>

        <Breadcrumbs
          separator={<NavigateNextIcon sx={{ fontSize: 18, color: "text.disabled" }} />}
          sx={{ flex: 1 }}
        >
          <Typography
            component={RouterLink}
            to="/dashboard"
            noWrap
            sx={{
              color: "text.secondary",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 600,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              "&:hover": { color: "primary.main" },
              transition: "color 0.15s ease",
            }}
          >
            BIGFOOT {(process.env.REACT_APP_SERVER_TYPE || "dealer").toUpperCase()} PORTAL
          </Typography>
          {detail ? (
            <Typography
              component={RouterLink}
              to={parentPath || "/dashboard"}
              noWrap
              sx={{
                color: "text.secondary",
                textDecoration: "none",
                fontSize: "0.875rem",
                fontWeight: 600,
                letterSpacing: "0.03em",
                textTransform: "uppercase",
                "&:hover": { color: "primary.main" },
                transition: "color 0.15s ease",
              }}
            >
              {title}
            </Typography>
          ) : (
            <Typography
              noWrap
              sx={{
                color: "text.primary",
                fontSize: "0.875rem",
                fontWeight: 700,
                letterSpacing: "0.03em",
                textTransform: "uppercase",
              }}
            >
              {title}
            </Typography>
          )}
          {detail && (
            <Typography
              noWrap
              sx={{
                color: "text.primary",
                fontSize: "0.875rem",
                fontWeight: 700,
                maxWidth: 300,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {detail}
            </Typography>
          )}
        </Breadcrumbs>

        {/* User menu */}
        <Box>
          <Stack
            component="button"
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={handleClick as any}
            sx={{
              cursor: "pointer",
              border: "none",
              bgcolor: "transparent",
              borderRadius: 2,
              px: 1,
              py: 0.5,
              "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: "primary.main",
                fontSize: "0.8rem",
                fontWeight: 700,
              }}
            >
              {userInitials || "?"}
            </Avatar>
            <Typography
              variant="body2"
              sx={{
                color: "text.primary",
                fontWeight: 500,
                display: { xs: "none", sm: "block" },
              }}
            >
              {userName || "User"}
            </Typography>
            <KeyboardArrowDownIcon
              sx={{ color: "text.secondary", fontSize: 20 }}
            />
          </Stack>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  minWidth: 160,
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                handleClose();
                logout();
              }}
            >
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
